import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './careReportBuilderPage.css';
import { Button } from './Button';
import { CareReportService } from '../services/supabase/careReportService';

const QUESTION_TYPES = [
  { value: 'text',     label: 'Short Answer' },
  { value: 'textarea', label: 'Long Answer' },
  { value: 'checkbox', label: 'Yes / No' },
  { value: 'number',   label: 'Number' },
];

const STANDARD_FIELDS = [
  { label: 'Caregiver',          note: 'auto-filled from login' },
  { label: 'Shift Start',        note: 'date + time' },
  { label: 'Shift End',          note: 'date + time' },
  { label: 'Mileage',            note: 'miles driven (number)' },
  { label: 'Supplies – Description', note: 'text' },
  { label: 'Supplies – Amount',  note: 'dollar amount' },
  { label: 'Important Information', note: 'text' },
];

export default function CareReportBuilderPage({ clientList, isAdmin }) {
  const navigate = useNavigate();

  const [selectedClient, setSelectedClient] = useState('');
  const [questions, setQuestions] = useState([]);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionType, setNewQuestionType] = useState('text');
  const [newQuestionRequired, setNewQuestionRequired] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusIsError, setStatusIsError] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // Load template when client selection changes
  useEffect(() => {
    if (!selectedClient) {
      setQuestions([]);
      return;
    }
    setLoading(true);
    setStatusMsg('');
    CareReportService.getTemplateForClient(selectedClient)
      .then(template => {
        setQuestions(template?.custom_questions ?? []);
      })
      .catch(() => {
        setStatusMsg('Failed to load template.');
        setStatusIsError(true);
      })
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const addQuestion = () => {
    if (!newQuestionText.trim()) return;
    const newQ = {
      id: crypto.randomUUID(),
      text: newQuestionText.trim(),
      type: newQuestionType,
      required: newQuestionRequired,
      order: questions.length + 1,
    };
    setQuestions(prev => [...prev, newQ]);
    setNewQuestionText('');
    setNewQuestionType('text');
    setNewQuestionRequired(false);
  };

  const removeQuestion = (id) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const saveTemplate = async () => {
    if (!selectedClient) {
      setStatusMsg('Please select a client first.');
      setStatusIsError(true);
      return;
    }
    setLoading(true);
    setStatusMsg('');
    try {
      const ordered = questions.map((q, i) => ({ ...q, order: i + 1 }));
      await CareReportService.upsertTemplate(selectedClient, ordered);
      setStatusMsg('Template saved successfully.');
      setStatusIsError(false);
    } catch {
      setStatusMsg('Failed to save template. Please try again.');
      setStatusIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const typeLabelFor = (value) => QUESTION_TYPES.find(t => t.value === value)?.label ?? value;

  const sortedClients = [...(clientList || [])].sort((a, b) =>
    a.name < b.name ? -1 : a.name > b.name ? 1 : 0
  );

  return (
    <div className="careReportBuilderPageDiv">
      <div className="careReportBuilderHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h2 className="careReportBuilderTitle">Care Report Builder</h2>
      </div>

      {/* Client selector */}
      <div className="formSep"></div>
      <div className="careBuilderSection">
        <label htmlFor="careClientSelect" className="careBuilderLabel">Select Client</label>
        <select
          id="careClientSelect"
          className="careClientSelect"
          value={selectedClient}
          onChange={e => setSelectedClient(e.target.value)}
        >
          <option value="">— choose a client —</option>
          {sortedClients.map(c => (
            <option key={c.docID} value={c.name}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Standard fields (informational) */}
      <div className="formSep"></div>
      <div className="careBuilderSection">
        <h3 className="careBuilderSectionTitle">Standard Fields (on every report)</h3>
        <ul className="standardFieldsList">
          {STANDARD_FIELDS.map(f => (
            <li key={f.label} className="standardFieldItem">
              <span className="standardFieldLabel">{f.label}</span>
              <span className="standardFieldNote">{f.note}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Custom questions */}
      {selectedClient && (
        <>
          <div className="formSep"></div>
          <div className="careBuilderSection">
            <h3 className="careBuilderSectionTitle">
              Custom Questions for <em>{selectedClient}</em>
            </h3>

            {loading && <p className="careBuilderLoading">Loading…</p>}

            {!loading && questions.length === 0 && (
              <p className="careBuilderEmpty">No custom questions yet. Add one below.</p>
            )}

            {!loading && questions.length > 0 && (
              <ul className="questionList">
                {questions.map((q, i) => (
                  <li key={q.id} className="questionItem">
                    <span className="questionIndex">{i + 1}.</span>
                    <span className="questionText">{q.text}</span>
                    <span className="questionTypeBadge">{typeLabelFor(q.type)}</span>
                    {q.required && <span className="questionRequired">Required</span>}
                    <button
                      className="removeQuestionBtn"
                      onClick={() => removeQuestion(q.id)}
                      title="Remove question"
                    >✕</button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add question form */}
            <div className="addQuestionForm">
              <h4 className="addQuestionTitle">Add Question</h4>
              <input
                type="text"
                className="newQuestionInput"
                placeholder="Question text…"
                value={newQuestionText}
                onChange={e => setNewQuestionText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addQuestion(); }}
              />
              <div className="addQuestionRow">
                <label htmlFor="newQuestionType">Type</label>
                <select
                  id="newQuestionType"
                  className="newQuestionTypeSelect"
                  value={newQuestionType}
                  onChange={e => setNewQuestionType(e.target.value)}
                >
                  {QUESTION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <label className="requiredCheckboxLabel">
                  <input
                    type="checkbox"
                    checked={newQuestionRequired}
                    onChange={e => setNewQuestionRequired(e.target.checked)}
                  />
                  {' '}Required
                </label>
                <Button
                  onClick={addQuestion}
                  type="button"
                  buttonStyle="btn--primary--solid"
                  buttonSize="btn--medium"
                >
                  + Add Question
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Save */}
      <div className="formSep"></div>
      <div className="careBuilderSection careBuilderSaveRow">
        <Button
          onClick={saveTemplate}
          type="button"
          buttonStyle="btn--success--solid"
          buttonSize="btn--medium"
          disabled={loading}
        >
          Save Template
        </Button>
        {statusMsg && (
          <p
            className="careBuilderStatus"
            style={{ color: statusIsError ? '#eb3f27' : '#2a9d2a' }}
          >
            {statusMsg}
          </p>
        )}
      </div>
    </div>
  );
}
